/**
 * ScanCard.tsx · 09_APP · OS-006 · 2026-10-20
 *
 * "Scan a card" on the Today page. Emily's ask, 2026-10-20 (D-0070, P-0008):
 * "I'd be taking a picture of their card, and you'd be using that picture to then build the profile and
 * automatically send the same thank you, welcome email." And: "That should be on the front dash page,
 * so when I go to it, it's right there."
 *
 * STEP ONE (v58): the camera opens inside the app, the photo is shrunk in the browser (hard rule 14:
 * never Supabase image transforms), stored in the private bucket `card-scans`, queued as waiting_to_read.
 * STEP TWO (v59, card-intake): the card is read with Claude vision and shown back to her as a check card.
 * 🔴 INSTANT, HER CALL (D-0070): scan -> saved -> thank-you sent, nothing left for her to finish. "Usually
 * we're in the midst of talking." Only a card with no readable email shows a check card.
 */

import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { os, OsError, type CardScan } from "../lib/os";
import { CardCheck } from "./CardCheck";

/** C54: the networking ask opens this same camera with the event filled in, so each card knows where she met them. */
const SCAN_EVENT = "c2c:scan-card";
export function openCardScanner(eventLabel: string) {
  window.dispatchEvent(new CustomEvent<string>(SCAN_EVENT, { detail: eventLabel }));
}

const MAX_SIDE = 1600;

async function shrink(file: File): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((ok, bad) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = () => bad(new Error("That photo could not be opened."));
      i.src = url;
    });
    const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(img.naturalWidth * scale);
    c.height = Math.round(img.naturalHeight * scale);
    c.getContext("2d")!.drawImage(img, 0, 0, c.width, c.height);
    return await new Promise<Blob>((ok, bad) =>
      c.toBlob((b) => (b ? ok(b) : bad(new Error("That photo could not be saved."))), "image/jpeg", 0.85)
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

const STATUS_WORDS: Record<string, string> = {
  waiting_to_read: "Saved, waiting to be read",
  read: "Saved, no thank-you",
  needs_check: "Needs your check",
  sent: "Thank-you sent",
  failed: "Could not be read",
  discarded: "Removed",
};

export function ScanCard() {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [recent, setRecent] = useState<Array<CardScan & { thumb?: string }>>([]);
  const eventLabel = useRef<string | null>(null);
  useEffect(() => {
    const h = (e: Event) => { eventLabel.current = (e as CustomEvent<string>).detail || null; input.current?.click(); };
    window.addEventListener(SCAN_EVENT, h);
    return () => window.removeEventListener(SCAN_EVENT, h);
  }, []);

  async function load() {
    try {
      const rows = await os.cardScans(3);
      // D-0091: a photo is fetched only for a card that still needs her check. A sent card shows one line, no picture.
      const withThumbs = await Promise.all(
        rows.map(async (r) => {
          if (r.status !== "needs_check" && r.status !== "failed") return r;
          const { data } = await supabase.storage.from("card-scans").createSignedUrl(r.storage_path, 600);
          return { ...r, thumb: data?.signedUrl };
        })
      );
      setRecent(withThumbs);
      return rows;
    } catch {
      /* the list is a convenience; a failure here must not hide the button */
    }
  }

  async function readWaiting() {
    try {
      setMsg("Reading the card...");
      const r = await os.cardRead();
      const lines = r.results.map((x) =>
        x.sent ? `Saved. Thank-you sent to ${x.name || x.email}.`
        : x.status === "needs_check" ? "Saved the photo, but no email was on the card. Type it below."
        : x.why ?? (x.error ? `Saved, but the thank-you did not go: ${x.error}` : "That card could not be read.")
      );
      setMsg(lines.length ? lines.join(" ") : null);
    } catch (x) {
      setMsg(null);
      setErr(x instanceof Error ? x.message : String(x));
    }
    await load();
  }

  useEffect(() => {
    load().then((rows) => { if (rows?.some((r) => r.status === "waiting_to_read")) readWaiting(); });
  }, []);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) throw new Error("You are signed out. Sign in and try again.");
      const blob = await shrink(file);
      const day = new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
      const path = `${u.user.id}/${day}/${crypto.randomUUID()}.jpg`;
      const up = await supabase.storage.from("card-scans").upload(path, blob, { contentType: "image/jpeg" });
      if (up.error) throw new Error(up.error.message);
      await os.cardScanAdd(path, eventLabel.current ?? undefined);
      await load();
      await readWaiting();
    } catch (x) {
      setErr(x instanceof OsError || x instanceof Error ? x.message : String(x));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="scan">
      <div className="scan-row">
        <div>
          <h3>Met someone?</h3>
          <p>Take a picture of their business card.</p>
        </div>
        <button className="scan-btn" onClick={() => { eventLabel.current = null; input.current?.click(); }} disabled={busy}>
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path fill="currentColor" d="M9 4 7.2 6H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-3.2L15 4H9Zm3 4.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Zm0 2a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z"/>
          </svg>
          {busy ? "Saving..." : "Scan a card"}
        </button>
        <input ref={input} type="file" accept="image/*" capture="environment" hidden onChange={onPick} />
      </div>
      {msg && <div className="scan-msg">{msg}</div>}
      {err && <div className="refusebox">{err}</div>}
      {recent.filter((r) => r.status === "needs_check" || r.status === "failed").map((r) => (
        <CardCheck key={r.id} scan={r} onDone={(m) => { setMsg(m); load(); }} />
      ))}
      {/* D-0091, her words 2026-10-21: "I don't need to see a picture of it... you could just put last thank you sent
          at such and such." One line, the most recent card that was handled. */}
      {(() => {
        const last = recent.find((r) => r.status !== "needs_check" && r.status !== "failed");
        if (!last) return null;
        const when = new Date(last.created_at).toLocaleString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
        const words = last.status === "sent" ? "Last thank-you sent" : `Last card: ${(STATUS_WORDS[last.status] ?? last.status).toLowerCase()}`;
        return <p className="scan-last">{words} {when}</p>;
      })()}
    </section>
  );
}
